import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { ProgressBar } from 'src/components/shared/RunProgress/ProgressBar';
import { formatRunMetric } from 'src/components/shared/RunProgress/runMetric';
import { CAMPAIGNS_COPY } from 'src/pages/Campaigns/constants/campaigns/copy';
import { useCampaignTraySocket } from 'src/pages/Campaigns/hooks/useCampaignWebSocket';
import { CampaignTrayRow } from 'src/pages/Campaigns/types/Campaigns';

export interface CampaignTrayProps {
  enabled: boolean;
  propertySlugs?: Record<number, string>;
}

/**
 * The floating campaign tray (DD-031). It shares the progress bar and the metric
 * formatter with the lottery tray; it is a separate shell because the lottery
 * shell owns batch-specific rows, and merging the shells is a follow-up recorded
 * under the design system's "gaps" list.
 */
export const CampaignTray: FC<CampaignTrayProps> = ({ enabled, propertySlugs = {} }) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<CampaignTrayRow[]>([]);
  const [dismissed, setDismissed] = useState(false);
  const [reconnectStopped, setReconnectStopped] = useState(false);

  const { reconnectStopped: stopped } = useCampaignTraySocket({
    enabled,
    onSnapshot: (incoming) => setRows(incoming as CampaignTrayRow[]),
    onCreated: (row) => setRows((current) => [...current, row as CampaignTrayRow]),
    onUpdated: (row) => {
      const typed = row as CampaignTrayRow;
      setRows((current) =>
        current.map((item) => (item.id === typed.id ? { ...item, ...typed } : item))
      );
    },
    onRemoved: (campaignId) =>
      setRows((current) => current.filter((item) => item.id !== campaignId)),
  });

  useEffect(() => {
    setReconnectStopped(stopped);
  }, [stopped]);

  const open = useCallback(
    (row: CampaignTrayRow): void => {
      const slug = row.property_slug || propertySlugs[row.property_id];
      if (slug) {
        navigate(`/workbench/${slug}/campaigns/${row.id}`);
      }
    },
    [navigate, propertySlugs]
  );

  if (!enabled || dismissed || rows.length === 0) {
    return null;
  }

  return (
    <Paper
      data-testid="campaign-tray"
      elevation={6}
      sx={{
        position: 'fixed',
        right: 24,
        bottom: 24,
        width: 320,
        p: 2,
        zIndex: 1200,
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          {CAMPAIGNS_COPY.tray.title}
        </Typography>
        <IconButton size="small" aria-label="Hide" onClick={() => setDismissed(true)}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {reconnectStopped && (
        <Typography variant="caption" sx={{ color: 'warning.dark', display: 'block', mb: 1 }}>
          {CAMPAIGNS_COPY.tray.paused}
        </Typography>
      )}

      {rows.map((row) => (
        <Box key={row.id} sx={{ mb: 1.5 }} data-testid={`tray-row-${row.id}`}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" sx={{ flex: 1 }} noWrap>
              {row.name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {formatRunMetric({ done: row.reached, total: row.included })}
            </Typography>
            <Tooltip title="Open campaign">
              <IconButton size="small" aria-label={`Open ${row.name}`} onClick={() => open(row)}>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <ProgressBar
            value={row.included > 0 ? row.reached / row.included : 0}
            label={`${row.name}: ${row.reached} of ${row.included} reached`}
          />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {row.state_display}
          </Typography>
        </Box>
      ))}
    </Paper>
  );
};

export default CampaignTray;
